import { useId, useState } from 'react';

import { toIso, toParts, type DateParts } from './dateParts';
import styles from './DateField.module.css';
import fieldStyles from './Field.module.css';
import { ErrorIcon } from './icons';

/**
 * A date entered as three parts: day, month, year.
 *
 * `<input type="date">` renders in the *browser's* locale, which nobody here
 * controls. The same stored value showed as 03/11/2008 to one reader and
 * 11/03/2008 to another, and under an Arabic locale some browsers offer a
 * Hijri picker. For a birth date on an insurance record that is not a cosmetic
 * problem: 3 November and 11 March are different people's paperwork.
 *
 * Three fields remove the question. Each part says what it is, the order is
 * fixed to the one the school writes, and the value handed out is always ISO.
 *
 * Kept in the design system rather than a feature: every date in the product
 * has to be read the same way (§3.1, §8.5).
 */

export interface DateFieldProps {
  readonly label: string;
  /** ISO `YYYY-MM-DD`, or empty. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  /*
   * `| undefined` is explicit because `exactOptionalPropertyTypes` is on and
   * callers pass values that may be absent.
   */
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly required?: boolean | undefined;
  readonly optional?: boolean | undefined;
  readonly disabled?: boolean | undefined;
}

export function DateField({
  label,
  value,
  onChange,
  hint,
  error,
  required,
  optional,
  disabled,
}: DateFieldProps) {
  const base = useId();
  const hintId = hint ? `${base}-hint` : undefined;
  const errorId = error ? `${base}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  /*
   * The typed parts, which survive being incomplete — `value` alone cannot
   * hold "the year so far", because a half-typed date has no ISO form.
   *
   * Re-derived when the ISO value changes from outside: loading a record, or
   * the form being reset after a save. Comparing against the last value seen
   * during render is React's own pattern for this; an effect would repaint the
   * fields a frame late.
   */
  const [draft, setDraft] = useState<DateParts>(() => toParts(value));
  const [lastValue, setLastValue] = useState(value);

  if (value !== lastValue) {
    setLastValue(value);
    setDraft(toParts(value));
  }

  const set = (key: keyof DateParts) => (raw: string) => {
    const next = { ...draft, [key]: raw.replace(/[^0-9]/g, '') };
    setDraft(next);

    /*
     * An incomplete date reports as empty rather than as the old one.
     *
     * Clearing the year to retype it must not leave the previous date standing
     * behind the form — that would save a value the fields no longer show.
     */
    onChange(toIso(next));
  };

  return (
    <div className={fieldStyles.field}>
      {/* A group rather than a label: three controls cannot share one `for`. */}
      <span className={fieldStyles.label} id={`${base}-label`}>
        {label}
        {required ? (
          <span className={fieldStyles.requiredMark}>
            <span aria-hidden="true">*</span>
            <span className="visually-hidden"> (مطلوب)</span>
          </span>
        ) : null}
        {optional && !required ? (
          <span className={fieldStyles.optionalMark}>(اختياري)</span>
        ) : null}
      </span>

      <div
        className={styles.parts}
        role="group"
        aria-labelledby={`${base}-label`}
        aria-describedby={describedBy}
      >
        <Part
          id={`${base}-day`}
          labelAr="يوم"
          placeholder="dd"
          maxLength={2}
          value={draft.day}
          invalid={Boolean(error)}
          disabled={disabled}
          onChange={set('day')}
        />
        <Part
          id={`${base}-month`}
          labelAr="شهر"
          placeholder="mm"
          maxLength={2}
          value={draft.month}
          invalid={Boolean(error)}
          disabled={disabled}
          onChange={set('month')}
        />
        <Part
          id={`${base}-year`}
          labelAr="سنة"
          placeholder="yyyy"
          maxLength={4}
          value={draft.year}
          invalid={Boolean(error)}
          disabled={disabled}
          onChange={set('year')}
        />
      </div>

      {error ? (
        <p className={fieldStyles.error} id={errorId}>
          <span className={fieldStyles.errorIcon}>
            <ErrorIcon />
          </span>
          <span>{error}</span>
        </p>
      ) : null}
      {hint ? (
        <p className={fieldStyles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function Part({
  id,
  labelAr,
  placeholder,
  maxLength,
  value,
  invalid,
  disabled,
  onChange,
}: {
  readonly id: string;
  readonly labelAr: string;
  readonly placeholder: string;
  readonly maxLength: number;
  readonly value: string;
  readonly invalid: boolean;
  readonly disabled: boolean | undefined;
  readonly onChange: (value: string) => void;
}) {
  return (
    <span className={styles.part}>
      <label className={styles.partLabel} htmlFor={id}>
        {labelAr}
      </label>
      <input
        id={id}
        className={styles.input}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </span>
  );
}
